import { Component, DoCheck, HostListener, OnDestroy, OnInit, Renderer2, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';

import { AppGlobals } from '@app/app.global';
import { AuthService, EntityService, SharedService } from '@app/core';
import { PmdTab } from '@app/shared';
import { DashboardService } from '@modules/dashboard/services';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, DoCheck, OnDestroy {
  @ViewChildren(PmdTab) tabs: PmdTab[];
  durationFilterForm: FormGroup;
  isBlankState: boolean = true;
  hideMobilePicker: boolean = true;
  duration: Date[] = [];
  today: Date = new Date();
  activeTabId: string;
  private destroy$: Subject<void> = new Subject<void>();
  dateFilterItem = [
    {
      name: 'Invoice Date',
      value: 'invoiceDate'
    },
    {
      name: 'Uploaded Date',
      value: 'createdAt'
    },
    {
      name: 'Reviewed Date',
      value: 'reviewedAt'
    }
  ]
  dateShow: boolean = true;

  // Show daterangepicker for mobile isuue
  @HostListener('window:scroll', ['$event'])
  onScroll(event: Event) {
    if (this.dateShow) {
      event.stopPropagation();
      const ele = document.getElementsByClassName("date-mobile")[0];

      if (ele && !this.hideMobilePicker) {
        ele.classList.add("show");
        ele.classList.add("mobile-picker");
      }
    }
  }

  constructor(public renderer: Renderer2,
    private dashboardService: DashboardService,
    public sharedService: SharedService,
    private formBuilder: FormBuilder,
    public authService: AuthService,
    public appGlobals: AppGlobals,
    private entityService: EntityService) {
    if (this.authService.user?.isAdmin) {
      window.location.reload();
      return;
    }
  }

  ngOnInit(): void {
    this.duration = this.getQuarter();
    this.entityService.entitiesChanges$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.getInvoiceCount();
    });

    this.createDurationFilterForm();
  }

  get f() { return this.durationFilterForm.controls; }

  get durationFilter() {
    const filter = {};
    if (this.f.startDate.value && this.f.endDate.value) {
      filter['startDate'] = new Date(this.f.startDate.value).toLocaleDateString('en-US');
      filter['endDate'] = new Date(this.f.endDate.value).toLocaleDateString('en-US');
      filter['dateField'] = this.f.dateFilter.value;
    }

    return filter;
  }

  ngDoCheck(): void {
    this.activeTabId = this.tabs?.find(tab => tab.active)?.id.split('-')?.[1] ?? '';
  }

  getInvoiceCount() {
    const count = parseInt(localStorage.getItem('invoiceCount')) || 0;

    if (count) {
      this.isBlankState = count <= this.appGlobals.minInvoiceForDashboard;
      return;
    }

    this.dashboardService.getInvoiceCount().subscribe((res) => {
      localStorage.setItem('invoiceCount', `${res.count}`);
      this.isBlankState = res.count <= this.appGlobals.minInvoiceForDashboard;
    }, (error: any) => this.sharedService.errorAlert(error));

  }

  createDurationFilterForm() {
    const [startDate, endDate] = this.getQuarter();
    this.durationFilterForm = this.formBuilder.group({
      dateFilter: [this.dateFilterItem[0].value],
      startDate: [startDate],
      endDate: [endDate],
      duration: [`${startDate.toLocaleDateString("en-US", this.appGlobals.dateFormat)} to ${endDate.toLocaleDateString("en-US", this.appGlobals.dateFormat)}`]
    });
  }

  isSameDay(d1: Date, d2: Date) {
    return d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate();
  }

  isCurrentQuarterSelected() {
    const [startDate, EndDate] = this.getQuarter();
    return this.isSameDay(startDate, this.duration[0]) &&
      this.isSameDay(EndDate, this.duration[1]);
  }

  getQuarterIndex(date = new Date()) {
    return Math.floor(date.getMonth() / 3);
  }

  getQuarter(date = new Date()) {
    return [
      new Date(date.getFullYear(), this.getQuarterIndex(date) * 3, 1),
      new Date(),
    ];
  }

  onDurationPickerOpen() {
    this.dateShow = false;
    setTimeout(() => { this.dateShow = true; }, 1);

    if (this.f.startDate.value && this.f.endDate.value) {
      this.duration = [
        this.f.startDate.value,
        this.f.endDate.value
      ];
    } else {
      this.duration = this.getQuarter();
    }
    // set flag for mobile isuue
    this.hideMobilePicker = false;
  }

  getDuration(predefinedDuration: string) {
    let duration: Date[] = [];
    switch (predefinedDuration) {
      case 'today':
        duration = [
          new Date(),
          new Date()
        ];
        break;
      case 'thisWeek':
        duration = [
          new Date(new Date().setDate(new Date().getDate() - new Date().getDay())),
          new Date()
        ];
        break;
      case 'lastWeek':
        const firstDayOfTheWeek = new Date(new Date().setDate(new Date().getDate() - new Date().getDay()));
        const lastDayOfTheWeek = new Date(new Date().setDate(new Date().getDate() - new Date().getDay() + 6));
        duration = [
          new Date(firstDayOfTheWeek.setDate(firstDayOfTheWeek.getDate() - 7)),
          new Date(lastDayOfTheWeek.setDate(lastDayOfTheWeek.getDate() - 7))
        ];
        break;
      case 'fortnight':
        duration = [
          new Date(new Date().setDate(new Date().getDate() - 15)),
          new Date()
        ];
        break;
      case 'MTD':
        duration = [
          new Date(new Date().setDate(1)),
          new Date()
        ];
        break;
      case 'YTD':
        duration = [
          new Date(new Date().getFullYear(), 0, 1),
          new Date()
        ];
        break;
      case 'custom':
        duration = this.duration;
        break;
      case 'clear':
        duration = this.getQuarter();
        break;
      case 'reset':
        duration = this.duration;
        break;
    }
    this.duration = duration;
  }

  getDaysDifference(startDate, endDate) {
    return Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1
  };

  setDuration() {
    if (this.duration?.length) {
      this.f.startDate.setValue(this.duration[0]);
      this.f.endDate.setValue(this.duration[1]);
      this.f.duration.setValue(
        this.duration[0].toLocaleDateString('en-US', this.appGlobals.dateFormat) + ' to ' +
        this.duration[1].toLocaleDateString('en-US', this.appGlobals.dateFormat)
      );
    } else {
      const [startDate, endDate] = this.getQuarter();
      this.f.startDate.setValue(startDate);
      this.f.endDate.setValue(endDate);
      this.f.duration.setValue(`${startDate.toLocaleDateString("en-US", this.appGlobals.dateFormat)} to ${endDate.toLocaleDateString("en-US", this.appGlobals.dateFormat)}`);
    }

    this.removeMobilePickerClass();
  }

  // Remove class for mobile isuue
  removeMobilePickerClass() {
    const ele = document.getElementsByClassName("date-mobile")[0];
    ele.classList.remove('mobile-picker');
    ele.classList.remove('show');
    this.hideMobilePicker = true;
  }

  durationChange(duration: Date[]) {
    this.duration = duration;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}